import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClassReportModel, ClassesReportModel, GetPayrollReportQuery } from '@bayclubs/group-classes-employee-api';
import { TranslateService } from '@ngx-translate/core';
import { PayrollCategorySummary, PayrollClassItem, PayrollInstructorSummary } from '../../models/payroll-report-model';
import { ReportFilter, ReportType } from '../../models/reports-model';
import { ReportSelectService } from '../../services/report-select.service';
import { ReportsService } from '../../services/reports.service';
import { BaseReportComponent } from '../base/base-report.component';

@Component({
    selector: 'app-payroll-report',
    templateUrl: './payroll-report.component.html',
    styleUrls: ['./payroll-report.component.scss'],
    standalone: false
})
export class PayrollReportComponent extends BaseReportComponent implements OnInit, OnDestroy {
    reportData: PayrollInstructorSummary[];

    constructor(
        activatedRoute: ActivatedRoute,
        reportSelectService: ReportSelectService,
        reportsService: ReportsService,
        translateService: TranslateService
    ) {
        super(activatedRoute, reportSelectService, reportsService, translateService);
    }

    ngOnInit() {
        this.reportType = ReportType.PayrollReport;
        super.ngOnInit();
        this.reportData = [];
    }
    ngOnDestroy() {
        super.ngOnDestroy();
    }

    private getClubId(data: string[] | string): string {
        if (!data) {
            return null;
        }

        if (typeof data === 'string') {
            return data;
        } else {
            if (data.length > 0) {
                return data[0];
            }
        }

        return null;
    }

    showReport(filter: ReportFilter) {
        const clubId = filter != null ? this.getClubId(filter.clubIds) : null;
        if (!filter || !clubId) {
            this.generateDisabled = true;
            this.reportData = [];
        } else {
            const payrollQuery: GetPayrollReportQuery = {
                classTypeId: filter.classTypeId,
                clubId: clubId,
                dateFrom: { value: filter.dateFrom.value, date: filter.dateFrom.value },
                dateTo: { value: filter.dateTo.value, date: filter.dateTo.value },
            };

            this.subscriptions.add(
                this.reportsService.getPayrollReportData(payrollQuery).subscribe((value: ClassesReportModel) => {
                    if (value.groupClasses !== null) {
                        this.generateReportData(value.groupClasses);
                        if (this.reportData.length > 0) {
                            this.generateDisabled = false;
                        }
                    }
                })
            );
        }
    }

    generateReportFile(filter: ReportFilter) {
        const clubId = this.getClubId(filter.clubIds);

        const payrollQuery: GetPayrollReportQuery = {
            classTypeId: filter.classTypeId,
            clubId: clubId,
            dateFrom: { value: filter.dateFrom.value, date: filter.dateFrom.value },
            dateTo: { value: filter.dateTo.value, date: filter.dateTo.value },
        };

        this.subscriptions.add(
            this.reportsService.generatePayrollReportFile(payrollQuery).subscribe(value => {
                this.downloadReportFile(value, 'labour_');
            })
        );
    }

    generateAdpExtract(filter: ReportFilter) {
        const clubId = this.getClubId(filter.clubIds);

        const payrollQuery: GetPayrollReportQuery = {
            classTypeId: filter.classTypeId,
            clubId: clubId,
            dateFrom: { value: filter.dateFrom.value, date: filter.dateFrom.value },
            dateTo: { value: filter.dateTo.value, date: filter.dateTo.value },
        };

        this.subscriptions.add(
            this.reportsService.generateAdpExtractReportFile(payrollQuery).subscribe(value => {
                this.downloadReportFile(value, 'adp_');
            })
        );
    }

    private generateReportData(groupClasses: ClassReportModel[]) {
        let data: PayrollInstructorSummary[] = [];
        groupClasses.forEach((groupClass: ClassReportModel) => {
            // find or create instructor summary item
            let instructorSummaryItem = data.find(p => p.instructorName === groupClass.instructorName);
            if (instructorSummaryItem === undefined) {
                instructorSummaryItem = new PayrollInstructorSummary(groupClass.instructorName, groupClass.instructorAdpId);
                data.push(instructorSummaryItem);
            }

            // find or create category summary item
            let categorySummaryItem = instructorSummaryItem.categories.find(
                x =>
                    groupClass.categoryCode === x.categoryCode &&
                    groupClass.hourlyRate === x.hourlyRate &&
                    groupClass.jobCostCode === x.jobCostCode
            );

            if (categorySummaryItem === undefined) {
                categorySummaryItem = new PayrollCategorySummary(
                    groupClass.categoryCode,
                    groupClass.category,
                    groupClass.jobCostCode,
                    groupClass.jobCostCodeDescription,
                    groupClass.hourlyRate
                );
                instructorSummaryItem.categories.push(categorySummaryItem);
            }

            const payrollItem = new PayrollClassItem();
            this.fillClassItem(groupClass, payrollItem);
            categorySummaryItem.classes.push(payrollItem);
        });
        data = this.calculateAndSortData(data);
        this.reportData = data;
    }
    private calculateAndSortData(data: PayrollInstructorSummary[]): PayrollInstructorSummary[] {
        return data.map(item => {
            item.categories = this.calculateAndSortCategory(item.categories);
            item.totalHours = item.categories.map(x => x.totalHours).reduce((acc, x) => acc + x, 0);
            item.totalCost = item.categories.map(x => x.totalCost).reduce((acc, x) => acc + x, 0);
            return item;
        });
    }

    private calculateAndSortCategory(data: PayrollCategorySummary[]): PayrollCategorySummary[] {
        return data.map(item => {
            item.totalHours = this.calculateSummaryRowTotalHours(item.classes);
            item.totalCost = this.calculateSummaryRowTotalCost(item.classes);
            item.classes = this.sort(item.classes, [{ date: 'asc', startTime: 'asc' }]);
            return item;
        });
    }
}
